import React, { useState, useRef, useEffect, useCallback } from 'react';
import useShopSettings from '../platform/settings/useShopSettings';
import './CatalogTab.css';

function newId() {
  return `ci-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

// ─── ItemRow ──────────────────────────────────────────────────────────────────

function ItemRow({
  item, defaultRate, onUpdate, onBlur, onDuplicate, onDelete,
  autoFocusName, menuOpen, menuRef, onMenuOpen, onMenuClose,
  canDrag, isDragOver, dragHandlers,
}) {
  const nameRef = useRef(null);

  useEffect(() => {
    if (autoFocusName && nameRef.current) nameRef.current.focus();
  }, [autoFocusName]);

  const effectiveRate = item.laborRate != null ? item.laborRate : defaultRate;
  const calcPrice = item.rateType === 'hourly'
    ? ((item.laborHours || 0) * (effectiveRate || 0)).toFixed(2)
    : null;

  function change(field, value) {
    onUpdate(item.id, field, value);
  }

  return (
    <tr className={[
      'sc-row',
      !item.active ? 'sc-row--inactive' : '',
      isDragOver ? 'sc-row--dragover' : '',
    ].filter(Boolean).join(' ')}>

      <td className="sc-td sc-td--handle" {...(canDrag ? dragHandlers : {})}>
        {canDrag && (
          <svg className="sc-handle-icon" width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
            <circle cx="3" cy="2.5" r="1.3"/><circle cx="7" cy="2.5" r="1.3"/>
            <circle cx="3" cy="7"   r="1.3"/><circle cx="7" cy="7"   r="1.3"/>
            <circle cx="3" cy="11.5" r="1.3"/><circle cx="7" cy="11.5" r="1.3"/>
          </svg>
        )}
      </td>

      <td className="sc-td sc-td--name">
        <input
          ref={nameRef}
          className="sc-input"
          value={item.name}
          placeholder="Item name…"
          onChange={e => change('name', e.target.value)}
          onBlur={onBlur}
        />
      </td>

      <td className="sc-td sc-td--desc">
        <input
          className="sc-input"
          value={item.description || ''}
          placeholder="Description…"
          onChange={e => change('description', e.target.value)}
          onBlur={onBlur}
        />
      </td>

      <td className="sc-td sc-td--type">
        <div className="sc-type-toggle">
          <button
            className={`sc-type-btn ${item.rateType === 'hourly' ? 'sc-type-btn--active' : ''}`}
            onMouseDown={e => e.preventDefault()}
            onClick={() => { change('rateType', 'hourly'); onBlur(); }}
          >HR</button>
          <button
            className={`sc-type-btn ${item.rateType === 'flat' ? 'sc-type-btn--active' : ''}`}
            onMouseDown={e => e.preventDefault()}
            onClick={() => { change('rateType', 'flat'); onBlur(); }}
          >Flat</button>
        </div>
      </td>

      <td className="sc-td sc-td--num">
        {item.rateType === 'hourly' ? (
          <input
            className="sc-input sc-input--num"
            type="number"
            min="0"
            step="0.5"
            value={item.laborHours ?? ''}
            placeholder="0"
            onChange={e => change('laborHours', e.target.value === '' ? null : parseFloat(e.target.value))}
            onBlur={onBlur}
          />
        ) : <span className="sc-na">—</span>}
      </td>

      <td className="sc-td sc-td--num">
        {item.rateType === 'hourly' ? (
          <div className="sc-prefixed">
            <span className="sc-prefix">$</span>
            <input
              className="sc-input sc-input--num"
              type="number"
              min="0"
              step="1"
              value={item.laborRate ?? ''}
              placeholder={defaultRate}
              onChange={e => change('laborRate', e.target.value === '' ? null : parseFloat(e.target.value))}
              onBlur={onBlur}
            />
          </div>
        ) : <span className="sc-na">—</span>}
      </td>

      <td className="sc-td sc-td--price">
        {item.rateType === 'hourly' ? (
          <span className="sc-calc">${calcPrice}</span>
        ) : (
          <div className="sc-prefixed">
            <span className="sc-prefix">$</span>
            <input
              className="sc-input sc-input--num"
              type="number"
              min="0"
              step="0.01"
              value={item.flatPrice ?? ''}
              placeholder="0.00"
              onChange={e => change('flatPrice', e.target.value === '' ? null : parseFloat(e.target.value))}
              onBlur={onBlur}
            />
          </div>
        )}
      </td>

      <td className="sc-td sc-td--active">
        <button
          className={`sc-toggle ${item.active ? 'sc-toggle--on' : ''}`}
          onMouseDown={e => e.preventDefault()}
          onClick={() => { change('active', !item.active); onBlur(); }}
          title={item.active ? 'Active' : 'Inactive'}
        >
          <span className="sc-toggle-thumb" />
        </button>
      </td>

      <td className="sc-td sc-td--menu">
        <div className="sc-menu-wrap" ref={menuRef}>
          <button className="sc-menu-btn" onMouseDown={e => e.preventDefault()} onClick={menuOpen ? onMenuClose : onMenuOpen}>•••</button>
          {menuOpen && (
            <div className="sc-menu">
              <button className="sc-menu-item" onClick={() => onDuplicate(item.id)}>Duplicate</button>
              <button className="sc-menu-item sc-menu-item--danger" onClick={() => onDelete(item.id)}>Delete</button>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

// ─── CatalogTab ───────────────────────────────────────────────────────────────

export default function CatalogTab() {
  const { settings, save } = useShopSettings();

  // Stable refs for use inside persist (avoids stale closures)
  const settingsRef = useRef(settings);
  const saveRef     = useRef(save);
  useEffect(() => { settingsRef.current = settings; }, [settings]);
  useEffect(() => { saveRef.current = save; },         [save]);

  const [cats, setCats] = useState(() =>
    settings.catalog?.categories?.length
      ? [...settings.catalog.categories]
      : [...(settings.catalog?.categories || [])]
  );
  const [items,   setItems]   = useState(() => settings.catalog?.items   || []);
  const [pricing, setPricing] = useState(() => ({
    defaultLaborRate: settings.pricing?.defaultLaborRate ?? 95,
    taxRate:          settings.pricing?.taxRate          ?? 0,
    partsMarkup:      settings.pricing?.partsMarkup      ?? 0,
  }));

  const catsRef    = useRef(cats);
  const itemsRef   = useRef(items);
  const pricingRef = useRef(pricing);
  useEffect(() => { catsRef.current    = cats;    }, [cats]);
  useEffect(() => { itemsRef.current   = items;   }, [items]);
  useEffect(() => { pricingRef.current = pricing; }, [pricing]);

  const [selCat,     setSelCat]     = useState(null);
  const [renameCat,  setRenameCat]  = useState(null);
  const [renameVal,  setRenameVal]  = useState('');
  const [addCatMode, setAddCatMode] = useState(false);
  const [addCatVal,  setAddCatVal]  = useState('');
  const [menuItemId, setMenuItemId] = useState(null);
  const [newItemId,  setNewItemId]  = useState(null);
  const [savedTs,    setSavedTs]    = useState(null);
  const dragIdRef = useRef(null);
  const [dragOver, setDragOver] = useState(null);
  const menuRef = useRef(null);

  // Close context menu on outside click
  useEffect(() => {
    if (!menuItemId) return;
    function handler(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuItemId(null);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuItemId]);

  // persist uses refs so it's always stable and never stale
  const persist = useCallback((newItems, newCats, newPricing) => {
    const s = settingsRef.current;
    const next = {
      ...s,
      catalog: {
        categories: newCats  ?? catsRef.current,
        items:      newItems ?? itemsRef.current,
      },
      pricing: newPricing ?? pricingRef.current,
    };
    saveRef.current(next).then(() => setSavedTs(Date.now()));
  }, []);

  // ── Item mutations ──────────────────────────────────────────────────────────

  function updateItem(id, field, value) {
    setItems(prev => {
      const next = prev.map(it => it.id === id ? { ...it, [field]: value } : it);
      itemsRef.current = next;
      return next;
    });
  }

  function addItem() {
    const cat = selCat ?? (catsRef.current[0] || 'General');
    const sortOrder = itemsRef.current.filter(it => it.category === cat).length;
    const id = newId();
    const item = {
      id, name: '', category: cat, description: '',
      rateType: 'hourly', laborHours: 1, laborRate: null,
      flatPrice: null, active: true, sortOrder,
    };
    const next = [...itemsRef.current, item];
    setItems(next);
    setNewItemId(id);
    // Don't persist until user blurs — the row is empty
  }

  function duplicateItem(id) {
    const src = itemsRef.current.find(it => it.id === id);
    if (!src) return;
    const sortOrder = itemsRef.current.filter(it => it.category === src.category).length;
    const copy = { ...src, id: newId(), name: `Copy of ${src.name}`, sortOrder };
    const next = [...itemsRef.current, copy];
    setItems(next);
    persist(next);
    setMenuItemId(null);
  }

  function deleteItem(id) {
    const next = itemsRef.current.filter(it => it.id !== id);
    setItems(next);
    persist(next);
    setMenuItemId(null);
  }

  function reorderItems(fromId, toId) {
    if (!fromId || !toId || fromId === toId) return;
    const visible = getVisibleItems(catsRef.current, itemsRef.current, selCat);
    const from = visible.findIndex(it => it.id === fromId);
    const to   = visible.findIndex(it => it.id === toId);
    if (from === -1 || to === -1) return;
    const reordered = [...visible];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    const withOrder = reordered.map((it, i) => ({ ...it, sortOrder: i }));
    const movedIds = new Set(withOrder.map(it => it.id));
    const next = [
      ...itemsRef.current.filter(it => !movedIds.has(it.id)),
      ...withOrder,
    ];
    setItems(next);
    persist(next);
  }

  // ── Pricing mutations ───────────────────────────────────────────────────────

  function updatePricing(field, value) {
    setPricing(prev => {
      const next = { ...prev, [field]: value };
      pricingRef.current = next;
      return next;
    });
  }

  function persistPricing() {
    persist(undefined, undefined, pricingRef.current);
  }

  // ── Category mutations ──────────────────────────────────────────────────────

  function commitAddCat() {
    const name = addCatVal.trim();
    setAddCatMode(false);
    setAddCatVal('');
    if (!name || catsRef.current.includes(name)) return;
    const next = [...catsRef.current, name];
    setCats(next);
    persist(undefined, next);
    setSelCat(name);
  }

  function commitRename() {
    const name = renameVal.trim();
    const old = renameCat;
    setRenameCat(null);
    if (!name || name === old || catsRef.current.includes(name)) return;
    const newCats  = catsRef.current.map(c => c === old ? name : c);
    const newItems = itemsRef.current.map(it => it.category === old ? { ...it, category: name } : it);
    setCats(newCats);
    setItems(newItems);
    if (selCat === old) setSelCat(name);
    persist(newItems, newCats);
  }

  function deleteCategory(cat) {
    if (!window.confirm(`Delete "${cat}"? Items in this category will be moved to Uncategorized.`)) return;
    const newCats = catsRef.current.filter(c => c !== cat);
    const needsUncategorized = itemsRef.current.some(it => it.category === cat)
      && !newCats.includes('Uncategorized');
    const finalCats = needsUncategorized ? [...newCats, 'Uncategorized'] : newCats;
    const newItems  = itemsRef.current.map(it =>
      it.category === cat ? { ...it, category: 'Uncategorized' } : it
    );
    setCats(finalCats);
    setItems(newItems);
    if (selCat === cat) setSelCat(null);
    persist(newItems, finalCats);
  }

  // ── Derived ─────────────────────────────────────────────────────────────────

  const visibleItems = getVisibleItems(cats, items, selCat);
  const catCounts = cats.reduce((acc, c) => {
    acc[c] = items.filter(it => it.category === c).length;
    return acc;
  }, {});
  const showSaved = savedTs && (Date.now() - savedTs < 2500);

  return (
    <div className="sc-wrap">

      {/* ── Shop Defaults ─────────────────────────────────────────────────── */}
      <div className="sc-defaults">
        <div className="sc-default-field">
          <span className="sc-default-label">Default Labor Rate</span>
          <div className="sc-prefixed sc-prefixed--default">
            <span className="sc-prefix">$</span>
            <input
              className="sc-default-input"
              type="number" min="0" step="0.01"
              value={pricing.defaultLaborRate}
              onChange={e => updatePricing('defaultLaborRate', parseFloat(e.target.value) || 0)}
              onBlur={persistPricing}
            />
          </div>
          <span className="sc-default-suffix">/hr</span>
        </div>
        <div className="sc-default-field">
          <span className="sc-default-label">Tax Rate</span>
          <input
            className="sc-default-input"
            type="number" min="0" max="100" step="0.01"
            value={pricing.taxRate}
            onChange={e => updatePricing('taxRate', parseFloat(e.target.value) || 0)}
            onBlur={persistPricing}
          />
          <span className="sc-default-suffix">%</span>
        </div>
        <div className="sc-default-field">
          <span className="sc-default-label">Parts Markup</span>
          <input
            className="sc-default-input"
            type="number" min="0" max="500" step="1"
            value={pricing.partsMarkup}
            onChange={e => updatePricing('partsMarkup', parseFloat(e.target.value) || 0)}
            onBlur={persistPricing}
          />
          <span className="sc-default-suffix">%</span>
        </div>
        <span className={`sc-saved-pill ${showSaved ? 'sc-saved-pill--visible' : ''}`}>✓ Saved</span>
      </div>

      {/* ── Two-column layout ─────────────────────────────────────────────── */}
      <div className="sc-layout">

        {/* Category sidebar */}
        <div className="sc-sidebar">
          <button
            className={`sc-cat-btn ${!selCat ? 'sc-cat-btn--active' : ''}`}
            onClick={() => setSelCat(null)}
          >
            <span className="sc-cat-name">All Items</span>
            <span className="sc-cat-count">{items.length}</span>
          </button>

          {cats.map(cat => (
            <div key={cat} className="sc-cat-row">
              {renameCat === cat ? (
                <input
                  className="sc-cat-rename"
                  value={renameVal}
                  autoFocus
                  onChange={e => setRenameVal(e.target.value)}
                  onBlur={commitRename}
                  onKeyDown={e => {
                    if (e.key === 'Enter')  commitRename();
                    if (e.key === 'Escape') setRenameCat(null);
                  }}
                />
              ) : (
                <button
                  className={`sc-cat-btn ${selCat === cat ? 'sc-cat-btn--active' : ''}`}
                  onClick={() => setSelCat(cat)}
                  onDoubleClick={() => { setRenameCat(cat); setRenameVal(cat); }}
                >
                  <span className="sc-cat-name">{cat}</span>
                  <span className="sc-cat-count">{catCounts[cat] || 0}</span>
                </button>
              )}
              <button
                className="sc-cat-del"
                onClick={() => deleteCategory(cat)}
                title="Delete category"
              >×</button>
            </div>
          ))}

          {addCatMode ? (
            <input
              className="sc-cat-add-input"
              value={addCatVal}
              autoFocus
              placeholder="Category name…"
              onChange={e => setAddCatVal(e.target.value)}
              onBlur={commitAddCat}
              onKeyDown={e => {
                if (e.key === 'Enter')  commitAddCat();
                if (e.key === 'Escape') { setAddCatMode(false); setAddCatVal(''); }
              }}
            />
          ) : (
            <button className="sc-add-cat" onClick={() => setAddCatMode(true)}>
              + Add Category
            </button>
          )}
        </div>

        {/* Table area */}
        <div className="sc-main">
          <div className="sc-table-bar">
            <span className="sc-table-title">
              {selCat || 'All Items'}
              <span className="sc-table-count"> ({visibleItems.length})</span>
            </span>
            {!selCat && (
              <span className="sc-drag-hint">Select a category to reorder items</span>
            )}
            <button className="sc-add-btn" onClick={addItem}>+ Add Item</button>
          </div>

          <div className="sc-table-scroll">
            <table className="sc-table">
              <colgroup>
                <col style={{ width: 28 }} />
                <col style={{ width: '22%' }} />
                <col />
                <col style={{ width: 80 }} />
                <col style={{ width: 64 }} />
                <col style={{ width: 88 }} />
                <col style={{ width: 96 }} />
                <col style={{ width: 50 }} />
                <col style={{ width: 34 }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="sc-th"></th>
                  <th className="sc-th">Name</th>
                  <th className="sc-th">Description</th>
                  <th className="sc-th sc-th--center">Type</th>
                  <th className="sc-th sc-th--right">Hours</th>
                  <th className="sc-th sc-th--right">Rate/hr</th>
                  <th className="sc-th sc-th--right">Price</th>
                  <th className="sc-th sc-th--center">Active</th>
                  <th className="sc-th"></th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((item) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    defaultRate={pricing.defaultLaborRate}
                    autoFocusName={item.id === newItemId && !item.name}
                    isDragOver={dragOver === item.id}
                    canDrag={!!selCat}
                    onUpdate={updateItem}
                    onBlur={() => persist(itemsRef.current)}
                    onDuplicate={duplicateItem}
                    onDelete={deleteItem}
                    menuOpen={menuItemId === item.id}
                    menuRef={menuItemId === item.id ? menuRef : null}
                    onMenuOpen={() => setMenuItemId(item.id)}
                    onMenuClose={() => setMenuItemId(null)}
                    dragHandlers={{
                      draggable: true,
                      onDragStart: (e) => {
                        dragIdRef.current = item.id;
                        e.dataTransfer.effectAllowed = 'move';
                      },
                      onDragEnd: () => {
                        reorderItems(dragIdRef.current, dragOver);
                        dragIdRef.current = null;
                        setDragOver(null);
                      },
                      onDragOver: (e) => {
                        e.preventDefault();
                        if (dragOver !== item.id) setDragOver(item.id);
                      },
                      onDragLeave: (e) => {
                        if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(null);
                      },
                    }}
                  />
                ))}
                {visibleItems.length === 0 && (
                  <tr>
                    <td colSpan={9} className="sc-empty">
                      No items yet — click <strong>+ Add Item</strong> to create one
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Helper (pure) ─────────────────────────────────────────────────────────────

function getVisibleItems(cats, items, selCat) {
  const filtered = selCat ? items.filter(it => it.category === selCat) : items;
  return filtered.slice().sort((a, b) => {
    if (!selCat) {
      const ci = cats.indexOf(a.category) - cats.indexOf(b.category);
      if (ci !== 0) return ci;
    }
    return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
  });
}
